import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Point } from '../../models/point';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';

export interface ContextMenuItem {
  id: string;
  label: string;
  // a muted line that says why there is nothing to do: no hover, no action, the menu stays open (0102)
  disabled?: boolean;
}

/**
 * Reusable right-click menu. No store — unlike DropdownComponent there is no async data here,
 * just a fixed list of actions. The parent renders it conditionally and positions it
 * (AddNodeDropdownComponent precedent).
 */
@Component({
  selector: 'context-menu',
  templateUrl: './context-menu.component.html',
  styleUrl: './context-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.left.px]': 'left',
    '[style.top.px]': 'top',
    '(clickOutside)': 'onClickOutside()',
    '(window:contextmenu)': 'onWindowContextMenu($event)'
  },
  hostDirectives: [
    {
      directive: ClickOutsideDirective,
      outputs: ['clickOutside']
    }
  ]
})
export class ContextMenuComponent {
  @Input({ required: true })
  items: ContextMenuItem[] = [];

  @Input({ required: true })
  set openAt(value: Point | undefined) {
    this.left = value?.x ?? 0;
    this.top = value?.y ?? 0;
  }

  @Output()
  readonly actionSelected = new EventEmitter<string>();

  @Output()
  readonly closed = new EventEmitter<void>();

  left = 0;
  top = 0;

  onItemClick(item: ContextMenuItem): void {
    if (item.disabled) {
      return;
    }

    this.actionSelected.emit(item.id);

    this.closed.emit();
  }

  onClickOutside(): void {
    this.closed.emit();
  }

  onWindowContextMenu(event: MouseEvent): void {
    // a handler already claimed this right-click and moved the menu — leave it be
    if (event.defaultPrevented) {
      return;
    }

    // right-click on the menu itself (the menu opens under the cursor, so "same point" lands here)
    // or on any unhandled area: close and let the native browser menu through
    this.closed.emit();
  }
}
